import { useCallback, useEffect, useState } from "react";
import { Loader2, Crown, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

// Hall of Fame: round-by-round MVPs + cumulative tally.
export default function MvpHistory({ poolId, currentUserId, t }) {
    const [loading, setLoading] = useState(true);
    const [rounds, setRounds] = useState([]);
    const [tally, setTally] = useState([]);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/pools/${poolId}/mvp-history`);
            setRounds(data.rounds || []);
            setTally(data.tally || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, [poolId]);

    useEffect(() => { reload(); }, [reload]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16" data-testid="mvp-loading">
                <Loader2 className="h-6 w-6 animate-spin text-[var(--accent-hex)]" />
            </div>
        );
    }

    return (
        <div data-testid="mvp-tab" className="space-y-6">
            <div>
                <h3 className="font-heading text-2xl font-bold uppercase text-[var(--text-primary)] flex items-center gap-2">
                    <Crown className="h-6 w-6 text-[var(--accent-hex)]" /> {t("mvp.title")}
                </h3>
                <p className="text-sm text-[var(--text-secondary)] mt-1">{t("mvp.subtitle")}</p>
            </div>

            {rounds.length === 0 ? (
                <div className="text-center py-16 text-[var(--text-secondary)]" data-testid="mvp-empty">
                    <Trophy className="h-10 w-10 text-[var(--accent-hex)] mx-auto mb-3 opacity-60" />
                    {t("mvp.no_finished")}
                </div>
            ) : (
                <>
                    {/* MVP tally — only render when at least one MVP recorded */}
                    {tally.length > 0 && (
                        <div className="rounded-lg border border-[var(--accent-border)] bg-[var(--accent-hex)]/5 p-4" data-testid="mvp-tally">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--accent-hex)] mb-3">{t("mvp.tally")}</div>
                            <div className="flex flex-wrap gap-2">
                                {tally.map((r) => {
                                    const isMe = r.user_id === currentUserId;
                                    return (
                                        <div key={r.user_id}
                                            className={`flex items-center gap-2 px-3 py-1.5 rounded border ${
                                                isMe ? "border-[var(--accent-hex)] bg-[var(--accent-hex)]/15" : "border-[var(--border-base)] bg-[var(--bg-card)]"
                                            }`}
                                            data-testid={`mvp-tally-row-${r.user_id}`}>
                                            <Crown className="h-3.5 w-3.5 text-[var(--accent-hex)]" />
                                            <span className="font-bold text-sm text-[var(--text-primary)]">{r.user_name}</span>
                                            <span className="font-heading text-xl font-extrabold text-[var(--accent-hex)] tabular-nums">{r.mvps}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Per-round table */}
                    <div className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] overflow-hidden">
                        <div className="hidden sm:flex items-center px-4 py-2 border-b border-[var(--border-muted)] text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">
                            <div className="w-44">{t("mvp.col.round")}</div>
                            <div className="w-24 text-center">{t("mvp.col.matches")}</div>
                            <div className="flex-1">{t("mvp.col.winner")}</div>
                            <div className="w-20 text-right">{t("mvp.col.points")}</div>
                        </div>
                        {rounds.map((r) => {
                            const winnerIsMe = r.winner?.user_id === currentUserId;
                            return (
                                <div key={r.round} className="flex flex-wrap sm:flex-nowrap items-center px-4 py-3 border-b border-[var(--border-muted)] last:border-b-0 gap-2"
                                    data-testid={`mvp-round-${r.round.replace(/\s+/g, "-")}`}>
                                    <div className="w-full sm:w-44">
                                        <div className="font-heading text-sm font-bold uppercase tracking-tight text-[var(--text-primary)]">{r.round}</div>
                                        {r.tied && r.winner && (
                                            <div className="text-[9px] uppercase tracking-widest font-bold text-amber-400 mt-0.5">{t("mvp.tied")}</div>
                                        )}
                                    </div>
                                    <div className="w-24 text-center">
                                        <span className="font-mono text-xs text-[var(--text-secondary)]">
                                            {r.matches_finished}/{r.matches_total}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        {r.winner ? (
                                            <div className={`flex items-center gap-2 ${winnerIsMe ? "text-[var(--accent-hex)]" : "text-[var(--text-primary)]"}`}>
                                                <Crown className="h-4 w-4 flex-shrink-0 text-[var(--accent-hex)]" />
                                                <span className="font-bold truncate">{r.winner.user_name}</span>
                                                {winnerIsMe && <span className="text-[9px] uppercase tracking-widest text-[var(--accent-hex)]">{t("leader.you")}</span>}
                                            </div>
                                        ) : (
                                            <span className="text-xs italic text-[var(--text-muted)]">{t("mvp.no_winner")}</span>
                                        )}
                                        {r.runner_up && (
                                            <div className="text-[10px] text-[var(--text-muted)] ml-6">
                                                {t("mvp.runner_up")}: <span className="text-[var(--text-secondary)]">{r.runner_up.user_name}</span> · {r.runner_up.points}p
                                            </div>
                                        )}
                                    </div>
                                    <div className="w-20 text-right">
                                        <span className="font-heading text-2xl font-extrabold text-[var(--accent-hex)] tabular-nums">{r.winner?.points ?? 0}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
